import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Check, ChevronRight, Clock, Info, LogOut,
  Rocket, Brain, Flame, Target, Trophy, BookOpen, Dumbbell, Code2,
  Feather, Sparkles, Compass, Zap, Plus, X, Lock, User, Users, UsersRound,
} from "lucide-react";
import { toast } from "sonner";
import { completeOnboardingProfile, getMe, getOnboardingProfile } from "@/lib/api";
import {
  USAGE_MODES, sectionsForMode, GOAL_TAGS, CHRONOTYPES, FOCUS_LENGTHS,
  PRODUCTIVITY_METHODS, CHALLENGES, DAYS, MEETING_FREQ, SESSION_DURATIONS,
  COMMUNICATION, ACCOUNTABILITY_STYLES, DISAPPEAR_ACTIONS, MOTIVATION_TYPES,
  LOOKING_FOR, PARTNER_LEVEL, GENDERS, DEAL_BREAKERS, INTERESTS,
  COMMITMENT_DURATIONS, ACTIVE_PARTNERSHIPS, PARTNERSHIP_INTENTS,
  PROFILE_ICONS, emptyProfile, partnershipIntentLabel,
} from "@/onboarding/data";
import {
  TextField, TextareaField, NumberField, SelectField, TagPicker,
  RadioCards, CheckList, SliderField, SwitchField, DayPicker, RangeSlider,
  WhyBox, FieldLabel, SearchableSelectField, SearchableMultiSelectField,
} from "@/onboarding/fields";
import { CITY_OPTIONS, COUNTRY_OPTIONS, TIMEZONE_OPTIONS } from "@/onboarding/locationOptions";
import {
  emptyGoal,
  migrateLegacyGoalAreas,
  normalizeGoals,
  syncLegacyGoalFields,
  uniqueGoalAreas,
} from "@/onboarding/goals";
import { getUser, setUser, getProfile, saveProfile, clearUser, clearSessionKeepProfile } from "@/lib/storage";
import BrandLogo from "@/components/BrandLogo";
import { pendingCohortJoin, takeReturn } from "@/lib/returnTo";

const ICON_MAP = { Rocket, Brain, Flame, Target, Trophy, BookOpen, Dumbbell, Code2, Feather, Sparkles, Compass, Zap };
const MODE_ICONS = { solo: User, partner: Users, community: UsersRound };

const nullableStringFields = [
  "usageMode", "fullName", "username", "gender", "customGender", "bio", "city", "country", "timezone",
  "customGoal", "goalDescription", "level", "target", "chronotype", "focusLength",
  "customChallenge", "meetingFrequency", "sessionDuration", "disappearAction",
  "motivationType", "lookingFor", "partnerLevel", "partnerDescription", "quote",
  "funFact", "commitmentDuration", "activePartnerships", "partnershipIntent",
];

function fromApiProfile(apiProfile, user) {
  const migrated = migrateLegacyGoalAreas({ ...emptyProfile(), ...apiProfile });
  const merged = syncLegacyGoalFields(migrated);
  nullableStringFields.forEach((field) => {
    if (merged[field] == null) merged[field] = "";
  });
  if (merged.age == null) merged.age = "";
  if (merged.targetDate == null) merged.targetDate = "";
  if (merged.availableStart == null) merged.availableStart = "07:00";
  if (merged.availableEnd == null) merged.availableEnd = "22:00";
  if (!merged.fullName && user?.fullName) merged.fullName = user.fullName;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!merged.timezone) merged.timezone = tz;
  } catch { /* noop */ }
  return merged;
}

function nullIfBlank(value) {
  return typeof value === "string" && value.trim() === "" ? null : value;
}

function numberOrNull(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toApiProfile(profile) {
  const payload = syncLegacyGoalFields({ ...profile });
  nullableStringFields.forEach((field) => {
    payload[field] = nullIfBlank(payload[field]);
  });
  payload.age = numberOrNull(payload.age);
  payload.targetDate = nullIfBlank(payload.targetDate);
  payload.goals = normalizeGoals(payload).map((goal) => ({
    id: goal.id,
    tags: goal.tags || [],
    customGoal: nullIfBlank(goal.customGoal),
    description: goal.description || "",
    target: nullIfBlank(goal.target),
    targetDate: nullIfBlank(goal.targetDate),
    importance: goal.importance || 7,
    isPrimary: Boolean(goal.isPrimary),
  }));
  payload.availableStart = nullIfBlank(payload.availableStart);
  payload.availableEnd = nullIfBlank(payload.availableEnd);
  payload.idealDay = (payload.idealDay || [])
    .filter((block) => block.start && block.end && block.label)
    .map((block) => ({
      id: block.id,
      start: block.start,
      end: block.end,
      label: block.label,
    }));
  delete payload.id;
  delete payload.userId;
  delete payload.createdAt;
  delete payload.updatedAt;
  delete payload.isComplete;
  return payload;
}

export default function Onboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editMode = searchParams.get("edit") === "1";
  const [user, setUserState] = useState(null);
  const [step, setStep] = useState(0);
  const [modeGateOpen, setModeGateOpen] = useState(true);
  const [profile, setProfile] = useState(emptyProfile());
  const [customChallenge, setCustomChallenge] = useState("");
  const [customDealBreaker, setCustomDealBreaker] = useState("");
  const [customLangSpoken, setCustomLangSpoken] = useState("");
  const [customLangPref, setCustomLangPref] = useState("");
  const [customInterest, setCustomInterest] = useState("");
  const [newBlock, setNewBlock] = useState({ start: "", end: "", label: "" });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const visibleSections = useMemo(() => sectionsForMode(profile.usageMode), [profile.usageMode]);

  const jumpTo = (key) => {
    const index = visibleSections.findIndex((section) => section.key === key);
    if (index >= 0) setStep(index);
  };

  useEffect(() => {
    let cancelled = false;
    const loadProfile = async () => {
      try {
        let u = getUser();
        if (!u) {
          u = await getMe();
          setUser(u);
          setUserState(u);
        } else {
          setUserState(u);
        }

        let remoteProfile = null;
        try {
          remoteProfile = await getOnboardingProfile();
        } catch {
          // Local drafts are authoritative during onboarding; a remote profile is only required for edit mode.
        }

        if (remoteProfile && (remoteProfile.completedAt || remoteProfile.isComplete) && !editMode) {
          navigate(takeReturn("/dashboard"));
          return;
        }

        let base;
        if (editMode && remoteProfile) {
          base = fromApiProfile(remoteProfile, u);
        } else {
          const local = getProfile();
          base = fromApiProfile(local || emptyProfile(), u);
          if (!editMode) base.usageMode = "";
        }

        if (!cancelled) {
          setProfile(base);
          saveProfile(base);
          setLoaded(true);
        }
      } catch (error) {
        const existing = getProfile();
        if (existing) {
          const base = fromApiProfile(existing, getUser());
          if (!editMode) base.usageMode = "";
          setProfile(base);
          setLoaded(true);
          toast("Using your local profile draft.", { description: "Backend profile could not be loaded." });
          return;
        }
        toast("Please sign in again.", { description: error.message });
        clearUser();
        navigate("/auth");
      }
    };

    loadProfile();
    return () => { cancelled = true; };
  }, [navigate, editMode]);

  useEffect(() => {
    if (!loaded) return;
    saveProfile(profile);
  }, [loaded, profile]);

  const update = (patch) => setProfile((p) => ({ ...p, ...patch }));

  const totalSteps = Math.max(1, visibleSections.length - 1); // exclude welcome from progress denominator base
  const progress = Math.round((step / totalSteps) * 100);
  const remainingMin = useMemo(() => {
    const perStep = 1.1;
    const left = Math.max(0, visibleSections.length - 1 - step);
    return Math.round(left * perStep);
  }, [step, visibleSections]);

  const canGoNext = () => {
    const section = visibleSections[step];
    if (!section) return true;
    // gentle validation on required core fields
    if (section.key === "basic") return profile.fullName.trim().length >= 2 && profile.username.trim().length >= 2 && Number(profile.age) >= 18;
    if (section.key === "goals") {
      const goals = normalizeGoals(profile);
      return profile.goalTags.length > 0 &&
        goals.length > 0 &&
        goals.every((goal) => goal.description.trim().length > 0);
    }
    if (section.key === "intent") return Boolean(profile.partnershipIntent);
    return true;
  };

  const goNext = () => {
    if (!canGoNext()) {
      toast("A few required fields need your attention.", { description: "Please fill the highlighted fields." });
      return;
    }
    if (step === visibleSections.length - 1) return submit();
    setStep((s) => Math.min(s + 1, visibleSections.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setSaving(true);
    try {
      const result = await completeOnboardingProfile(toApiProfile(profile));
      const completed = fromApiProfile(result.profile, user);
      saveProfile(completed);
      try { localStorage.removeItem("hm_ob_step"); localStorage.removeItem("hm_ob_step_key"); } catch { /* noop */ }
      const soloComplete = profile.usageMode === "solo";
      toast("Profile complete.", { description: soloComplete ? "Your solo workspace is ready." : "You're now in the matching pool." });
      navigate(takeReturn("/dashboard"));
    } catch (error) {
      toast("Profile could not be completed.", { description: error.message });
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    clearSessionKeepProfile();
    navigate("/");
  };

  const handleModeSelect = (mode) => {
    update({ usageMode: mode });
    try { localStorage.removeItem("hm_ob_step"); localStorage.removeItem("hm_ob_step_key"); } catch { /* noop */ }
    setStep(0);
    setModeGateOpen(false);
  };

  if (!user || !loaded) return null;

  if (modeGateOpen) {
    return (
      <ModeGate
        saving={saving}
        onLogout={logout}
        onSelect={handleModeSelect}
        initialMode={profile.usageMode}
        editMode={editMode}
      />
    );
  }

  const section = visibleSections[step];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0]">
      {/* Top bar */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo />
            <span className="font-serif-editorial text-xl tracking-tight">HustlrMate</span>
            <span className="hidden sm:inline text-xs text-[#A1A1AA] ml-2">/ Building profile</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 text-xs text-[#A1A1AA]">
              <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
              {saving ? "Saving..." : `~${remainingMin} min left`}
            </div>
            <button onClick={logout} data-testid="ob-logout" className="text-xs text-[#A1A1AA] hover:text-[#F5F5F0] inline-flex items-center gap-1.5">
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
              Sign out
            </button>
          </div>
        </div>
        {/* progress bar */}
        <div className="max-w-6xl mx-auto px-6 sm:px-10 pb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] tracking-[0.24em] uppercase text-[#A1A1AA]">Step {step} of {visibleSections.length - 1}</span>
            <span className="text-[10px] text-[#A1A1AA]" data-testid="ob-progress-pct">{progress}%</span>
          </div>
          <div className="h-0.5 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-[#F59E0B] transition-all duration-500 ease-out" style={{ width: `${progress}%` }} data-testid="ob-progress-bar" />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10 grid lg:grid-cols-[240px_1fr] gap-10">
        {/* Sidebar step list */}
        <aside className="hidden lg:block">
          <div className="sticky top-32">
            <div className="overline mb-4">Sections</div>
            <ol className="space-y-1">
              {visibleSections.map((s, i) => {
                const done = i < step;
                const active = i === step;
                return (
                  <li key={s.key}>
                    <button
                      onClick={() => setStep(i)}
                      data-testid={`ob-nav-${s.key}`}
                      className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                        active ? "bg-[#F59E0B]/10 text-[#F5F5F0]" : "text-[#A1A1AA] hover:text-[#F5F5F0]"
                      }`}
                    >
                      <span className={`inline-flex items-center justify-center w-5 h-5 rounded-sm text-[10px] font-serif-editorial ${
                        done ? "bg-[#F59E0B] text-black" : active ? "border border-[#F59E0B] text-[#F59E0B]" : "border border-white/10 text-[#A1A1AA]"
                      }`}>
                        {done ? <Check className="w-3 h-3" strokeWidth={3} /> : s.overline.replace("0", "")}
                      </span>
                      <span className="font-light">{s.title}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>
        </aside>

        {/* Main content */}
        <main>
          <div className="mb-8">
            <div className="overline mb-3">Section {section.overline} — {section.title}</div>
            <h1 className="font-serif-editorial font-light tracking-tighter text-4xl sm:text-5xl leading-[1.05]">
              {section.key === "welcome" && (<>Let's build your <em className="italic text-[#F59E0B]">Accountability Profile.</em></>)}
              {section.key === "basic" && <>Who are you, really?</>}
              {section.key === "goals" && <>What are you <em className="italic text-[#F59E0B]">chasing?</em></>}
              {section.key === "work" && <>How do you <em className="italic text-[#F59E0B]">operate?</em></>}
              {section.key === "schedule" && <>When do you show up?</>}
              {section.key === "accountability" && <>How should we hold you <em className="italic text-[#F59E0B]">to it?</em></>}
              {section.key === "partner" && <>Who are you looking for?</>}
              {section.key === "personality" && <>The <em className="italic text-[#F59E0B]">human</em> behind the hustle.</>}
              {section.key === "commitment" && <>How long are you in?</>}
              {section.key === "intent" && <>What kind of connection are you <em className="italic text-[#F59E0B]">open to?</em></>}
              {section.key === "review" && <>One last look.</>}
            </h1>
          </div>

          <div className="fade-up">
            {section.key === "welcome" && <StepWelcome onBegin={goNext} mode={profile.usageMode} />}
            {section.key === "basic" && <StepBasic profile={profile} update={update} />}
            {section.key === "goals" && <StepGoals profile={profile} update={update} />}
            {section.key === "work" && <StepWork profile={profile} update={update} mode={profile.usageMode} customChallenge={customChallenge} setCustomChallenge={setCustomChallenge} newBlock={newBlock} setNewBlock={setNewBlock} />}
            {section.key === "schedule" && <StepSchedule profile={profile} update={update} mode={profile.usageMode} />}
            {section.key === "accountability" && <StepAccountability profile={profile} update={update} />}
            {section.key === "partner" && <StepPartner profile={profile} update={update} customDealBreaker={customDealBreaker} setCustomDealBreaker={setCustomDealBreaker} customLangSpoken={customLangSpoken} setCustomLangSpoken={setCustomLangSpoken} customLangPref={customLangPref} setCustomLangPref={setCustomLangPref} />}
            {section.key === "personality" && <StepPersonality profile={profile} update={update} customInterest={customInterest} setCustomInterest={setCustomInterest} />}
            {section.key === "commitment" && <StepCommitment profile={profile} update={update} />}
            {section.key === "intent" && <StepIntent profile={profile} update={update} />}
            {section.key === "review" && <StepReview profile={profile} onEdit={jumpTo} />}
          </div>

          {section.key !== "welcome" && (
            <div className="flex items-center justify-between mt-12 pt-6 border-t border-white/5">
              <button onClick={goBack} data-testid="ob-back" className="hm-btn-ghost inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button onClick={goNext} data-testid="ob-next" className="hm-btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold">
                {section.key === "review" ? "Submit profile" : "Continue"}
                <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

/* ============ STEPS ============ */

function ModeGate({ saving, onLogout, onSelect, initialMode = "", editMode = false }) {
  // Someone who arrived from a cohort link needs Partner mode to be paired, so start them there.
  const joiningCohort = !editMode && Boolean(pendingCohortJoin());
  const [selected, setSelected] = useState(initialMode || (joiningCohort ? "partner" : ""));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0]">
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo />
            <span className="font-serif-editorial text-xl tracking-tight">HustlrMate</span>
            <span className="hidden sm:inline text-xs text-[#A1A1AA] ml-2">{editMode ? "/ Edit profile" : "/ Choose your mode"}</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 text-xs text-[#A1A1AA]">
              <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
              {saving ? "Saving..." : "Before we begin"}
            </div>
            <button onClick={onLogout} data-testid="ob-logout" className="text-xs text-[#A1A1AA] hover:text-[#F5F5F0] inline-flex items-center gap-1.5">
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
              Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 sm:px-10 py-16">
        <div className="overline mb-4">{editMode ? "Profile setup" : "Before we begin"}</div>
        <h1 className="font-serif-editorial font-light tracking-tighter text-4xl sm:text-5xl leading-[1.05] mb-6">
          How do you want to use <em className="italic text-[#F59E0B]">HustlrMate?</em>
        </h1>
        <p className="text-[#A1A1AA] font-light leading-relaxed max-w-2xl">
          {editMode
            ? "You're editing your profile. Your existing answers are preserved, and you can change them on the following steps."
            : "We'll tailor your onboarding around the way you plan to work. Your choice now simply determines which questions we ask first — it doesn't lock you out of anything."}
        </p>

        <div className="grid sm:grid-cols-3 gap-4 mt-10">
          {USAGE_MODES.map((mode) => {
            const Icon = MODE_ICONS[mode.key];
            const active = selected === mode.key;
            return (
              <button
                key={mode.key}
                type="button"
                onClick={() => setSelected(mode.key)}
                data-testid={`ob-mode-${mode.key}`}
                className={`text-left p-6 rounded-md border transition-all ${
                  active
                    ? "border-[#F59E0B] bg-[#F59E0B]/[0.06] text-[#F5F5F0]"
                    : "border-white/10 bg-white/[0.02] text-[#A1A1AA] hover:border-white/25 hover:text-[#F5F5F0]"
                }`}
              >
                <Icon className={`w-6 h-6 mb-5 ${active ? "text-[#F59E0B]" : ""}`} strokeWidth={1.5} />
                <div className="font-serif-editorial text-xl text-[#F5F5F0]">{mode.title}</div>
                <div className="text-xs font-light leading-relaxed mt-2">{mode.description}</div>
              </button>
            );
          })}
        </div>

        {joiningCohort && (
          <p className="mt-6 text-sm text-[#F5F5F0] font-light" data-testid="ob-cohort-hint">
            You're joining a cohort, so we've picked <span className="text-[#F59E0B]">Partner</span>: cohorts pair you with someone. Finish these steps and you're added automatically.
          </p>
        )}

        <div className="p-4 rounded-md border border-[#F59E0B]/20 bg-[#F59E0B]/[0.04] text-sm text-[#F5F5F0] font-light mt-8 flex gap-3">
          <Info className="w-4 h-4 mt-0.5 text-[#F59E0B] shrink-0" strokeWidth={1.5} />
          <div>
            <span className="text-[#F59E0B]">Choosing one mode won't restrict the others.</span> You can switch between Solo, Partner, and Group/Community anytime later — this just sets the starting point for your onboarding.
          </div>
        </div>

        <button
          type="button"
          onClick={() => selected && onSelect(selected)}
          disabled={!selected}
          data-testid="ob-mode-continue"
          className={`mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm font-semibold ${
            selected ? "hm-btn-primary" : "bg-white/5 text-[#5b5b60] cursor-not-allowed"
          }`}
        >
          Continue <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
}

function StepWelcome({ onBegin, mode }) {
  const solo = mode === "solo";
  return (
    <div className="hm-card p-8 sm:p-12 rounded-md max-w-3xl">
      <div className="flex items-start gap-4 mb-6">
        <Lock className="w-6 h-6 text-[#F59E0B] mt-1" strokeWidth={1.5} />
        <div>
          <h2 className="font-serif-editorial text-2xl sm:text-3xl leading-tight mb-3">
            {solo
              ? "Solo mode keeps you locked in — your goals, your schedule, your consistency."
              : "Unlike social media, your profile here decides who you'll spend weeks — sometimes months — working with."}
          </h2>
          <p className="text-[#A1A1AA] font-light leading-relaxed">
            {solo
              ? <>We ask a focused set of questions about <span className="text-[#F5F5F0]">you</span> — your goals, work style, and schedule — so your solo dashboard can actually hold you accountable.</>
              : <>We ask a lot of questions — not to collect data for its own sake, but because <span className="text-[#F5F5F0]">better profiles lead to better matches.</span> This isn't a swipe-and-hope app. This is a commitment layer.</>}
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 my-8">
        <Stat n={solo ? "4–6" : "7–10"} label="Minutes total" />
        <Stat n={solo ? "5" : "9"} label="Sections" />
        <Stat n="1" label="Sitting" />
      </div>

      <div className="p-4 rounded-md border border-[#F59E0B]/20 bg-[#F59E0B]/[0.04] text-sm text-[#F5F5F0] font-light mb-8 flex gap-3">
        <Info className="w-4 h-4 mt-0.5 text-[#F59E0B] shrink-0" strokeWidth={1.5} />
        <div>
          <span className="text-[#F59E0B]">Please complete this in one sitting.</span> You won't be able to {solo ? "track your productivity" : "start matching"} until your profile is complete. We save progress locally as you go — but this only works if you finish.
        </div>
      </div>

      <button onClick={onBegin} data-testid="ob-begin" className="hm-btn-primary inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm font-semibold">
        Let's Begin <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div className="p-5 rounded-md border border-white/5 bg-white/[0.02]">
      <div className="font-serif-editorial text-3xl text-[#F5F5F0]">{n}</div>
      <div className="text-xs text-[#A1A1AA] tracking-[0.18em] uppercase mt-1">{label}</div>
    </div>
  );
}

function StepBasic({ profile, update }) {
  const solo = profile.usageMode === "solo";
  return (
    <div className="space-y-8 max-w-3xl">
      <WhyBox>{solo ? "Your name, age, and location make your profile feel like you. Your timezone calibrates your solo dashboard to when you actually work." : "Your name and age help partners see a real person. Your city and timezone let us calibrate matches to when you actually work."}</WhyBox>

      <IconPicker value={profile.profileIcon} onChange={(v) => update({ profileIcon: v })} />

      <div className="grid sm:grid-cols-2 gap-6">
        <TextField label="Full name" required value={profile.fullName} onChange={(v) => update({ fullName: v })} placeholder="Ada Lovelace" maxLength={40} testId="ob-fullname" hint={solo ? "Your first name is shown on your profile." : "Your first name is shown to potential partners."} />
        <TextField label="Username" required value={profile.username} onChange={(v) => update({ username: v.replace(/\s+/g, "").toLowerCase() })} placeholder="ada.hustles" maxLength={24} testId="ob-username" hint="Unique. Shown as your handle." />
      </div>

      <div className="grid sm:grid-cols-3 gap-6">
        <NumberField label="Age" value={profile.age} onChange={(v) => update({ age: v })} min={18} max={100} placeholder="24" testId="ob-age" hint="Used for compatibility, not display." />
        <SelectField label="Gender" value={profile.gender} onChange={(v) => update({ gender: v })} options={GENDERS} testId="ob-gender" />
        {profile.gender === "Custom" && (
          <TextField label="Custom gender" value={profile.customGender} onChange={(v) => update({ customGender: v })} placeholder="Describe" testId="ob-custom-gender" />
        )}
      </div>

      <TextareaField label="Bio" maxLength={300} rows={3} value={profile.bio} onChange={(v) => update({ bio: v })} placeholder="I've been preparing for UPSC for 8 months and want someone serious about daily study sessions." testId="ob-bio" hint={solo ? "A few sentences about you and your current focus." : "A few sentences. This is the first thing a partner reads."} />

      <div className="grid sm:grid-cols-3 gap-6">
        <SearchableSelectField label="City" value={profile.city} onChange={(v) => update({ city: v })} options={CITY_OPTIONS} placeholder="Search or type your city" testId="ob-city" />
        <SearchableSelectField label="Country" value={profile.country} onChange={(v) => update({ country: v })} options={COUNTRY_OPTIONS} placeholder="Search country" testId="ob-country" />
        <SearchableSelectField label="Timezone" value={profile.timezone} onChange={(v) => update({ timezone: v })} options={TIMEZONE_OPTIONS} placeholder="Search timezone" testId="ob-timezone" hint="Auto-detected. Editable." />
      </div>
    </div>
  );
}

function IconPicker({ value, onChange }) {
  return (
    <div>
      <FieldLabel hint="Pick an icon that represents your energy. You can change this later.">Profile icon</FieldLabel>
      <div className="grid grid-cols-6 sm:grid-cols-12 gap-2" data-testid="ob-icon-picker">
        {PROFILE_ICONS.map((k) => {
          const Icon = ICON_MAP[k];
          const active = value === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => onChange(k)}
              title={k}
              className={`aspect-square rounded-md border flex items-center justify-center transition-all ${
                active ? "bg-[#F59E0B] text-black border-[#F59E0B]" : "border-white/10 text-[#A1A1AA] hover:text-[#F5F5F0] hover:border-white/20"
              }`}
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepGoals({ profile, update }) {
  const solo = profile.usageMode === "solo";
  const [customGoalArea, setCustomGoalArea] = useState("");
  const goals = normalizeGoals(profile);
  const updateGoals = (nextGoals) => {
    const synced = syncLegacyGoalFields({ ...profile, goals: nextGoals });
    update({
      goals: synced.goals,
      goalTags: synced.goalTags,
      customGoal: synced.customGoal,
      goalDescription: synced.goalDescription,
      level: synced.level,
      target: synced.target,
      targetDate: synced.targetDate || "",
      importance: synced.importance,
      isPrimary: synced.isPrimary,
    });
  };
  const updateGoal = (id, patch) => updateGoals(goals.map((goal) => goal.id === id ? { ...goal, ...patch } : goal));
  const updateGoalAreas = (goalTags) => {
    const nextGoalTags = uniqueGoalAreas(goalTags);
    const synced = syncLegacyGoalFields({
      ...profile,
      goalTags: nextGoalTags,
      goals: goals.map((goal) => ({ ...goal, tags: nextGoalTags, customGoal: "" })),
    });
    update({
      goals: synced.goals,
      goalTags: synced.goalTags,
      customGoal: "",
      goalDescription: synced.goalDescription,
      target: synced.target,
      targetDate: synced.targetDate || "",
      importance: synced.importance,
    });
  };
  const addGoal = () => updateGoals([...goals, emptyGoal({ id: Date.now(), isPrimary: goals.length === 0 })]);
  const removeGoal = (id) => {
    const remaining = goals.filter((goal) => goal.id !== id);
    updateGoals(remaining.length > 0 ? remaining : [emptyGoal({ id: Date.now(), isPrimary: true })]);
  };
  const setPrimary = (id) => updateGoals(goals.map((goal) => ({ ...goal, isPrimary: goal.id === id })));

  return (
    <div className="space-y-8 max-w-3xl">
      <WhyBox>{solo ? "Add each active goal separately. Your primary goal anchors your solo dashboard and daily tracking." : "Add each active goal separately. Your primary goal still anchors matching, but secondary goals help us find partners with overlapping ambition."}</WhyBox>

      <div className="hm-card p-5 rounded-md">
        <TagPicker
          label="Goal areas"
          hint="Select every area that applies once. These areas describe your overall journey; the details for each individual goal come next."
          options={GOAL_TAGS}
          value={profile.goalTags}
          onChange={updateGoalAreas}
          customValue={customGoalArea}
          setCustomValue={setCustomGoalArea}
          customPlaceholder="Add your own goal area..."
          testId="ob-goal-areas"
        />
      </div>

      {goals.map((goal, index) => (
        <div key={goal.id} className="hm-card p-5 rounded-md space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="overline mb-1">Goal {index + 1}</div>
              <div className="text-sm text-[#A1A1AA]">{goal.isPrimary ? (solo ? "Primary goal" : "Primary matching goal") : "Secondary goal"}</div>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setPrimary(goal.id)} className={`px-3 py-1.5 rounded-md text-xs border ${goal.isPrimary ? "bg-[#F59E0B] text-black border-[#F59E0B]" : "border-white/10 text-[#A1A1AA] hover:text-[#F5F5F0]"}`}>
                Primary
              </button>
              {goals.length > 1 && (
                <button type="button" onClick={() => removeGoal(goal.id)} className="hm-btn-ghost px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1">
                  <X className="w-3 h-3" /> Remove
                </button>
              )}
            </div>
          </div>

          <TextareaField label="Describe this goal" rows={3} maxLength={500} value={goal.description} onChange={(v) => updateGoal(goal.id, { description: v })} placeholder="I want to crack GATE CSE in 2027." testId={`ob-goal-desc-${index}`} />

          <div className="grid sm:grid-cols-2 gap-6">
            <TextField label="Concrete target" value={goal.target} onChange={(v) => updateGoal(goal.id, { target: v })} placeholder="Lose 15 kg / Ship v1 / Publish paper" testId={`ob-target-${index}`} />
            <TextField label="Target date (optional)" type="date" value={goal.targetDate} onChange={(v) => updateGoal(goal.id, { targetDate: v })} testId={`ob-target-date-${index}`} />
          </div>

          <SliderField label="How serious are you about this goal?" min={1} max={10} value={goal.importance} onChange={(v) => updateGoal(goal.id, { importance: v })} leftLabel="A whim" rightLabel="Life-defining" testId={`ob-importance-${index}`} />
        </div>
      ))}

      <button type="button" onClick={addGoal} className="hm-btn-ghost inline-flex items-center gap-2 px-4 py-3 rounded-md text-sm">
        <Plus className="w-4 h-4" /> Add another goal
      </button>
    </div>
  );
}

function StepWork({ profile, update, mode, customChallenge, setCustomChallenge, newBlock, setNewBlock }) {
  const solo = mode === "solo";
  const blockPresets = ["Work", "Study", "Health / fitness", "Sleep", "Deep work", "Meals", "Commute"];
  const addBlock = () => {
    if (!newBlock.start || !newBlock.end || !newBlock.label.trim()) { toast("Choose a label and time interval first."); return; }
    if (newBlock.end <= newBlock.start) { toast("End time must be after start time."); return; }
    update({ idealDay: [...profile.idealDay, { ...newBlock, id: Date.now() }] });
    setNewBlock({ start: "", end: "", label: "" });
  };
  const removeBlock = (id) => update({ idealDay: profile.idealDay.filter((b) => b.id !== id) });
  const toggleNonNeg = (id) => {
    const set = new Set(profile.nonNegotiableBlocks);
    set.has(id) ? set.delete(id) : set.add(id);
    update({ nonNegotiableBlocks: [...set] });
  };
  return (
    <div className="space-y-8 max-w-3xl">
      <WhyBox>{solo ? "Understanding how you operate is what makes solo tracking actually stick — your methods, energy, and honest blockers." : "This section is gold for matching. Two people with the same goal but opposite work styles rarely last a month."}</WhyBox>

      <RadioCards label="Which describes you best?" options={CHRONOTYPES} value={profile.chronotype} onChange={(v) => update({ chronotype: v })} columns={3} testId="ob-chronotype" />

      <SliderField label="Realistic focused hours per day" min={0} max={16} value={profile.dailyHours} onChange={(v) => update({ dailyHours: v })} leftLabel="0h" rightLabel="16h" format={(v) => `${v}h`} testId="ob-daily-hours" />

      <div className="grid sm:grid-cols-2 gap-6">
        <SelectField label="Preferred focus session length" value={profile.focusLength} onChange={(v) => update({ focusLength: v })} options={FOCUS_LENGTHS} testId="ob-focus-length" />
      </div>

      <CheckList label="Preferred productivity method" options={PRODUCTIVITY_METHODS} value={profile.productivityMethods} onChange={(v) => update({ productivityMethods: v })} columns={3} testId="ob-productivity" />

      <TagPicker
        label="Your biggest challenges"
        options={CHALLENGES}
        value={profile.challenges}
        onChange={(v) => update({ challenges: v })}
        customValue={customChallenge}
        setCustomValue={setCustomChallenge}
        customPlaceholder="Add another…"
        testId="ob-challenges"
      />

      <TextareaField label="What usually causes you to miss your goals?" rows={3} maxLength={400} value={profile.missGoalsStory} onChange={(v) => update({ missGoalsStory: v })} placeholder={solo ? "Be honest. This helps your solo plan know when to push and when to ease off." : "Be honest. This helps a partner know when to push and when to pull back."} testId="ob-miss-story" />

      <div>
        <FieldLabel hint="Add your regular occupied blocks, then mark the non-negotiable ones.">Describe your ideal productive day</FieldLabel>
        <p className="mb-3 max-w-2xl text-xs leading-relaxed text-[#A1A1AA]">{solo ? "Fill this in accurately. It becomes the backbone of your solo dashboard and daily focus blocks." : "Fill this in accurately. Along with your availability and timezone, it helps us show genuine free-time overlap with potential partners. Empty or vague schedules cannot get a reliable overlap score."}</p>
        <div className="mb-4 flex flex-wrap gap-2" aria-label="Quick activity labels">
          {blockPresets.map((label) => <button key={label} type="button" onClick={() => setNewBlock({ ...newBlock, label })} className={`rounded-md border px-3 py-1.5 text-xs transition ${newBlock.label === label ? "border-[#F59E0B] bg-[#F59E0B]/10 text-[#F5F5F0]" : "border-white/10 text-[#A1A1AA] hover:border-white/30"}`}>{label}</button>)}
        </div>
        <div className="grid grid-cols-[100px_100px_1fr_auto] gap-2 items-end mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1">Start</div>
            <input type="time" value={newBlock.start} onChange={(e) => setNewBlock({ ...newBlock, start: e.target.value })} className="hm-input w-full px-2 py-2 rounded-md text-sm" data-testid="ob-block-start" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1">End</div>
            <input type="time" value={newBlock.end} onChange={(e) => setNewBlock({ ...newBlock, end: e.target.value })} className="hm-input w-full px-2 py-2 rounded-md text-sm" data-testid="ob-block-end" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1">Activity</div>
            <input type="text" value={newBlock.label} onChange={(e) => setNewBlock({ ...newBlock, label: e.target.value })} placeholder="Deep work / Gym / Study…" className="hm-input w-full px-3 py-2 rounded-md text-sm" data-testid="ob-block-label" />
          </div>
          <button type="button" onClick={addBlock} data-testid="ob-block-add" className="hm-btn-ghost px-4 py-2 rounded-md text-xs inline-flex items-center gap-1">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
        <div className="space-y-2">
          {profile.idealDay.length === 0 && (
            <div className="text-xs text-[#7a7a80] italic font-light py-3">No blocks yet. Add your first one above.</div>
          )}
          {profile.idealDay.map((b) => {
            const isNN = profile.nonNegotiableBlocks.includes(b.id);
            return (
              <div key={b.id} className={`flex items-center gap-3 p-3 rounded-md border ${isNN ? "border-[#F59E0B]/40 bg-[#F59E0B]/5" : "border-white/10 bg-[#0f0f0f]"}`}>
                <span className="font-serif-editorial text-sm text-[#F59E0B] w-24">{b.start} → {b.end}</span>
                <span className="flex-1 text-sm">{b.label}</span>
                <button type="button" onClick={() => toggleNonNeg(b.id)} className={`text-[10px] px-2 py-1 rounded-sm uppercase tracking-wider ${isNN ? "bg-[#F59E0B] text-black" : "border border-white/10 text-[#A1A1AA]"}`}>
                  Non-negotiable
                </button>
                <button type="button" onClick={() => removeBlock(b.id)} className="text-[#7a7a80] hover:text-[#F5F5F0]">
                  <X className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StepSchedule({ profile, update, mode }) {
  const solo = mode === "solo";
  return (
    <div className="space-y-8 max-w-3xl">
      <WhyBox>{solo ? "Your availability turns into your solo focus plan — protect the hours that matter." : "Two people in different timezones with no overlap can love the same goal and still fail. Overlap is oxygen."}</WhyBox>

      <DayPicker label="Your working days" days={DAYS} value={profile.workingDays} onChange={(v) => update({ workingDays: v })} testId="ob-workdays" />

      <div className={solo ? "max-w-md" : "grid sm:grid-cols-2 gap-6"}>
        <div>
          <FieldLabel hint={solo ? "When you're generally focused and productive." : "When you're generally available for co-working or check-ins."}>Available window (local time)</FieldLabel>
          <div className="grid grid-cols-2 gap-3">
            <input type="time" value={profile.availableStart} onChange={(e) => update({ availableStart: e.target.value })} className="hm-input px-3 py-3 rounded-md text-sm" data-testid="ob-avail-start" />
            <input type="time" value={profile.availableEnd} onChange={(e) => update({ availableEnd: e.target.value })} className="hm-input px-3 py-3 rounded-md text-sm" data-testid="ob-avail-end" />
          </div>
        </div>
        {!solo && <SelectField label="Meeting frequency" value={profile.meetingFrequency} onChange={(v) => update({ meetingFrequency: v })} options={MEETING_FREQ} testId="ob-meet-freq" />}
      </div>

      {!solo && <SelectField label="Preferred session duration" value={profile.sessionDuration} onChange={(v) => update({ sessionDuration: v })} options={SESSION_DURATIONS} testId="ob-session-dur" />}

      {!solo && <CheckList label="Preferred communication" options={COMMUNICATION} value={profile.communication} onChange={(v) => update({ communication: v })} columns={3} testId="ob-communication" />}

      {!solo && (
        <SearchableMultiSelectField
          label="Comfortable timezones (partner-side)"
          hint="Match candidates who live in these zones."
          options={TIMEZONE_OPTIONS}
          value={profile.comfortableTimezones}
          onChange={(v) => update({ comfortableTimezones: v })}
          placeholder="Search timezone"
          testId="ob-comfortable-tz"
        />
      )}
    </div>
  );
}

function StepAccountability({ profile, update }) {
  return (
    <div className="space-y-8 max-w-3xl">
      <WhyBox>This is our USP. Most apps track what you do. HustlrMate tracks who's noticing. Get this section right.</WhyBox>

      <CheckList label="How should your partner keep you accountable?" options={ACCOUNTABILITY_STYLES} value={profile.accountabilityStyle} onChange={(v) => update({ accountabilityStyle: v })} columns={2} testId="ob-acc-style" />

      <RadioCards label="If you disappear for 3 days — what should your partner do?" options={DISAPPEAR_ACTIONS} value={profile.disappearAction} onChange={(v) => update({ disappearAction: v })} columns={2} testId="ob-disappear" />

      <RadioCards label="Which type of accountability motivates you most?" options={MOTIVATION_TYPES} value={profile.motivationType} onChange={(v) => update({ motivationType: v })} columns={2} testId="ob-motivation" />

      <SliderField label="What kind of partner are you?" leftLabel="Very chill" rightLabel="Extremely strict" min={1} max={10} value={profile.strictness} onChange={(v) => update({ strictness: v })} testId="ob-strictness" />

      <SliderField label="How honest are you willing to be about your day?" leftLabel="Only wins" rightLabel="Everything" min={1} max={10} value={profile.honesty} onChange={(v) => update({ honesty: v })} testId="ob-honesty" />

      <div className="grid sm:grid-cols-2 gap-4">
        <SwitchField label="OK sharing progress photos / screenshots?" value={profile.shareProgress} onChange={(v) => update({ shareProgress: v })} testId="ob-share-progress" />
        <SwitchField label="Open to shared goals with your partner?" value={profile.sharedGoals} onChange={(v) => update({ sharedGoals: v })} testId="ob-shared-goals" />
      </div>
    </div>
  );
}

function StepPartner({ profile, update, customDealBreaker, setCustomDealBreaker, customLangSpoken, setCustomLangSpoken, customLangPref, setCustomLangPref }) {
  return (
    <div className="space-y-8 max-w-3xl">
      <WhyBox>Some people need a domain twin. Others need a workhorse of any domain. Be honest — flexibility opens the pool.</WhyBox>

      <RadioCards label="What are you looking for in a partner?" options={LOOKING_FOR} value={profile.lookingFor} onChange={(v) => update({ lookingFor: v })} columns={2} testId="ob-looking-for" />

      <RadioCards label="Should your partner be at your level?" options={PARTNER_LEVEL} value={profile.partnerLevel} onChange={(v) => update({ partnerLevel: v })} columns={4} testId="ob-partner-level" />

      <RangeSlider label="Preferred age range" min={18} max={70} value={profile.ageRange} onChange={(v) => update({ ageRange: v })} testId="ob-age-range" />

      <SelectField label="Preferred gender" value={profile.partnerGender} onChange={(v) => update({ partnerGender: v })} options={[...GENDERS, "No Preference"]} testId="ob-partner-gender" />

      <TagPicker
        label="Languages you speak"
        options={["English", "Hindi", "Spanish", "Mandarin", "French", "German", "Japanese", "Arabic", "Portuguese", "Tamil"]}
        value={profile.languagesSpoken}
        onChange={(v) => update({ languagesSpoken: v })}
        customValue={customLangSpoken}
        setCustomValue={setCustomLangSpoken}
        customPlaceholder="Add language…"
        testId="ob-lang-spoken"
      />

      <TagPicker
        label="Languages you'd like your partner to know"
        options={["English", "Hindi", "Spanish", "Mandarin", "French", "German", "Japanese", "Arabic", "Portuguese", "Tamil"]}
        value={profile.languagesPreferred}
        onChange={(v) => update({ languagesPreferred: v })}
        customValue={customLangPref}
        setCustomValue={setCustomLangPref}
        customPlaceholder="Add language…"
        testId="ob-lang-pref"
      />

      <SliderField label="Minimum daily commitment expected" min={1} max={12} value={profile.minCommitment} onChange={(v) => update({ minCommitment: v })} leftLabel="1h" rightLabel="12h" format={(v) => `${v}h`} testId="ob-min-commit" />

      <SliderField label="Maximum timezone difference" min={0} max={12} value={profile.maxTimezoneDiff} onChange={(v) => update({ maxTimezoneDiff: v })} leftLabel="0h" rightLabel="12h" format={(v) => `±${v}h`} testId="ob-max-tz-diff" />

      <TagPicker
        label="Deal breakers"
        hint="Traits you refuse to tolerate."
        options={DEAL_BREAKERS}
        value={profile.dealBreakers}
        onChange={(v) => update({ dealBreakers: v })}
        customValue={customDealBreaker}
        setCustomValue={setCustomDealBreaker}
        customPlaceholder="Add a deal breaker…"
        testId="ob-deal-breakers"
      />

      <TextareaField label="What do you hope your partner is like?" rows={3} maxLength={400} value={profile.partnerDescription} onChange={(v) => update({ partnerDescription: v })} placeholder="A morning person who's building something and doesn't sugarcoat feedback." testId="ob-partner-desc" />
    </div>
  );
}

function StepPersonality({ profile, update, customInterest, setCustomInterest }) {
  const solo = profile.usageMode === "solo";
  const sliders = [
    { k: "introExtro", left: "Introvert", right: "Extrovert" },
    { k: "plannerSpont", left: "Planner", right: "Spontaneous" },
    { k: "compCoop", left: "Competitive", right: "Cooperative" },
    { k: "seriousFun", left: "Serious", right: "Fun" },
    { k: "optRealist", left: "Optimistic", right: "Realistic" },
    { k: "logicEmo", left: "Logical", right: "Emotional" },
  ];
  return (
    <div className="space-y-8 max-w-3xl">
      <WhyBox>{solo ? "Personality doesn't decide fit — but it decides how your dashboard, reminders, and nudges should feel." : "Personality doesn't decide fit — but it decides how much you'll <em>enjoy</em> the partnership."}</WhyBox>

      <div className="space-y-6">
        {sliders.map((s) => (
          <SliderField key={s.k} label={`${s.left} vs ${s.right}`} leftLabel={s.left} rightLabel={s.right} min={1} max={10} value={profile[s.k]} onChange={(v) => update({ [s.k]: v })} testId={`ob-${s.k}`} />
        ))}
      </div>

      <TagPicker
        label="Your interests"
        options={INTERESTS}
        value={profile.interests}
        onChange={(v) => update({ interests: v })}
        customValue={customInterest}
        setCustomValue={setCustomInterest}
        customPlaceholder="Add an interest…"
        testId="ob-interests"
      />

      <TextField label="Favorite quote (optional)" value={profile.quote} onChange={(v) => update({ quote: v })} placeholder="Discipline equals freedom." testId="ob-quote" />

      <TextField label="Fun fact (optional)" value={profile.funFact} onChange={(v) => update({ funFact: v })} placeholder="I've read 30 books this year." testId="ob-funfact" />
    </div>
  );
}

function StepCommitment({ profile, update }) {
  return (
    <div className="space-y-8 max-w-3xl">
      <WhyBox>Psychology matters. Publicly stating your commitment window makes you 3× more likely to follow through.</WhyBox>

      <RadioCards label="How long are you willing to commit?" options={COMMITMENT_DURATIONS} value={profile.commitmentDuration} onChange={(v) => update({ commitmentDuration: v })} columns={3} testId="ob-commit-duration" />

      <RadioCards label="How many partnerships can you actively maintain?" options={ACTIVE_PARTNERSHIPS} value={profile.activePartnerships} onChange={(v) => update({ activePartnerships: v })} columns={4} testId="ob-active-parts" />
    </div>
  );
}

function StepIntent({ profile, update }) {
  return (
    <div className="space-y-8 max-w-3xl">
      <WhyBox>Clear intentions prevent mismatched expectations. Your answer is shown on your profile and can be used in matching and filters. Accountability remains the foundation; friendship, networking, or romance should only develop with mutual interest and explicit boundaries.</WhyBox>
      <RadioCards
        label="What kind of connection are you looking for?"
        hint="Choose the closest fit. You can change this later in Settings → Profile. Openness never implies consent or obligation."
        options={PARTNERSHIP_INTENTS}
        value={profile.partnershipIntent}
        onChange={(v) => update({ partnershipIntent: v })}
        columns={2}
        testId="ob-partnership-intent"
      />
    </div>
  );
}

function StepReview({ profile, onEdit }) {
  const goals = normalizeGoals(profile);
  const solo = profile.usageMode === "solo";
  const scheduleItems = solo
    ? [
      ["Working days", profile.workingDays.join(", ")],
      ["Available", `${profile.availableStart}–${profile.availableEnd}`],
    ]
    : [
      ["Working days", profile.workingDays.join(", ")],
      ["Available", `${profile.availableStart}–${profile.availableEnd}`],
      ["Frequency", profile.meetingFrequency],
      ["Session", profile.sessionDuration],
      ["Communication", profile.communication.join(", ")],
    ];

  const rows = [
    { section: "Basic", jump: "basic", items: [
      ["Name", profile.fullName], ["Username", `@${profile.username}`], ["Age", profile.age],
      ["Gender", profile.gender === "Custom" ? profile.customGender : profile.gender],
      ["Location", [profile.city, profile.country].filter(Boolean).join(", ")],
      ["Timezone", profile.timezone],
    ]},
    { section: "Goals", jump: "goals", items: [
      ["Total goals", goals.length],
      ["Primary", (goals.find((goal) => goal.isPrimary) || goals[0])?.description],
      ["Areas", (profile.goalTags || []).join(", ")],
      ["Targets", goals.map((goal) => [goal.target, goal.targetDate && `by ${goal.targetDate}`].filter(Boolean).join(" ")).filter(Boolean).join(" | ")],
    ]},
    { section: "Work Style", jump: "work", items: [
      ["Chronotype", profile.chronotype],
      ["Daily hours", `${profile.dailyHours}h`],
      ["Focus length", profile.focusLength],
      ["Methods", profile.productivityMethods.join(", ")],
      ["Challenges", profile.challenges.join(", ")],
      ["Ideal day", `${profile.idealDay.length} blocks (${profile.nonNegotiableBlocks.length} non-negotiable)`],
    ]},
    { section: "Schedule", jump: "schedule", items: scheduleItems },
    ...(solo ? [] : [
      { section: "Accountability", jump: "accountability", items: [
        ["Styles", profile.accountabilityStyle.join(", ")],
        ["If I disappear", profile.disappearAction],
        ["Motivation", profile.motivationType],
        ["Strictness", `${profile.strictness} / 10`],
        ["Honesty", `${profile.honesty} / 10`],
        ["Share progress", profile.shareProgress ? "Yes" : "No"],
        ["Shared goals", profile.sharedGoals ? "Yes" : "No"],
      ]},
      { section: "Partner", jump: "partner", items: [
        ["Looking for", profile.lookingFor],
        ["Level", profile.partnerLevel],
        ["Age range", profile.ageRange.join("–")],
        ["Gender", profile.partnerGender],
        ["Languages", profile.languagesPreferred.join(", ")],
        ["Min commit", `${profile.minCommitment}h`],
        ["Max TZ diff", `±${profile.maxTimezoneDiff}h`],
        ["Deal breakers", profile.dealBreakers.join(", ")],
      ]},
    ]),
    { section: "Personality", jump: "personality", items: [
      ["Interests", profile.interests.join(", ")],
      ["Quote", profile.quote], ["Fun fact", profile.funFact],
    ]},
    ...(solo ? [] : [
      { section: "Commitment", jump: "commitment", items: [
        ["Duration", profile.commitmentDuration],
        ["Active partnerships", profile.activePartnerships],
      ]},
      { section: "Connection Intent", jump: "intent", items: [
        ["Looking for", partnershipIntentLabel(profile.partnershipIntent)],
      ]},
    ]),
  ];
  return (
    <div className="space-y-6 max-w-4xl">
      <WhyBox>Take one last look. You can jump to any section to edit.</WhyBox>
      {rows.map((r) => (
        <div key={r.section} className="hm-card p-6 rounded-md">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">{r.section}</div>
            <button onClick={() => onEdit(r.jump)} className="text-xs text-[#F59E0B] hover:underline inline-flex items-center gap-1">
              Edit <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {r.items.map(([k, v]) => (
              <div key={k} className="flex gap-3 text-sm">
                <dt className="text-[#A1A1AA] font-light w-32 shrink-0">{k}</dt>
                <dd className="text-[#F5F5F0] font-light break-words">{v || <span className="text-[#5b5b60] italic">—</span>}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
}
